import "./Dashboard.css";
import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Menu,
  Layout,
  Alert,
  Button,
  Dropdown,
  Space,
  message,
  Spin,
} from "antd";
import {
  DownOutlined,
  UserOutlined,
  GlobalOutlined,
  ShopOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PhoneOutlined,
  LogoutOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { AppContext } from "../../context/AppContextProvider";
import AppRoutes, { ContentPages } from "../../routes";
import { useLocation, useNavigate } from "react-router-dom";
import StoreConfiguration from "../Content/StoreConfiguration/StoreConfiguration";
import Subscriptions from "../Content/Subscriptions/Subscriptions";
import { useTranslation } from "react-i18next";
import {
  updateAuthDetails,
  userLogout,
  getinvoices,
} from "../../network/network";
import { handleApiError, showAppError } from "../../utils/errorHandler";
import { filterPagesByAddons } from "../../utils/utils";
import Home from "../Content/Home/Home";
import {
  CompanyStatus,
  InvoiceStatus,
  AddonName,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
  TRIAL_DURATION,
  UserType,
} from "../../configs/constants";
import Invoices from "../Content/Invoices/Invoices";
import moment from "moment";
import PendingQrOrderNotifications from "../../components/PendingQrOrderNotifications";
import ZatcaCsidExpiryBanner from "../../components/ZatcaCsidExpiryBanner/ZatcaCsidExpiryBanner";
import { getEntityBranchId } from "../../utils/branchScope";
import { getActiveMenuKeyByPath } from "../../utils/navigation";
import {
  PORTAL_LANGUAGES,
  buildPortalAppConfig,
  portalLanguageLabel,
  resolvePortalLanguage,
} from "../../utils/portalLanguage";

const { Header, Sider, Content } = Layout;

const normalizeWarnings = (warnings) => {
  if (!warnings) return [];
  if (Array.isArray(warnings)) return warnings;
  if (typeof warnings === "object") {
    if (warnings.message || warnings.code) return [warnings];
    return Object.values(warnings);
  }
  if (typeof warnings === "string" && warnings.trim()) {
    return [{ message: warnings }];
  }
  return [];
};

const FORCED_PAGE_PATHS = {
  HOME: "/",
  STORE_CONFIGURATION: "/store-configuration",
  SUBSCRIPTION: "/subscriptions",
  INVOICES: "/invoices",
};

const Dashboard = () => {
  const {
    authUser,
    setAuthUser,
    appConfig,
    setAppConfigWrapper,
    fullScreenPOS,
    isSiderCollapsed,
    setIsSiderCollapsed,
  } = useContext(AppContext);
  const [showPageOnly, setShowPageOnly] = useState(false);
  const [blockRouting, setBlockRouting] = useState(false);
  const [anyUnpaidInvoice, setAnyUnpaidInvoice] = useState(false);
  const [deviceWidth, setDeviceWidth] = useState(() => window.innerWidth);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarPages = useMemo(
    () =>
      filterPagesByAddons(ContentPages, authUser).map((page) =>
        page.subMenus
          ? {
              ...page,
              subMenus: filterPagesByAddons(page.subMenus, authUser),
            }
          : page,
      ),
    [authUser],
  );
  const activeMenuKey = useMemo(
    () => getActiveMenuKeyByPath(location.pathname, sidebarPages),
    [location.pathname, sidebarPages],
  );
  const showDesktopSidebar = deviceWidth > 768 && !fullScreenPOS;
  const sidebarOffset = showDesktopSidebar
    ? isSiderCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_WIDTH
    : 0;

  useEffect(() => {
    if (
      authUser.company_status === CompanyStatus.Active ||
      authUser.company_status === CompanyStatus.Kyc ||
      authUser.company_status === CompanyStatus.Review
    ) {
      setBlockRouting(false);
      setShowPageOnly(false);
    } else if (
      authUser.company_status === CompanyStatus.SubscriptionEnded ||
      authUser.company_status === CompanyStatus.SubscriptionInReview
    ) {
      setBlockRouting(true);
      setShowPageOnly("SUBSCRIPTION");
    } else if (
      authUser.company_status === CompanyStatus.Blocked ||
      authUser.company_status === CompanyStatus.SubscriptionInvoiceGenerated
    ) {
      setBlockRouting(true);
      setShowPageOnly("INVOICES");
    } else {
      setBlockRouting(false);
      setShowPageOnly(false);
    }

    setAppConfigWrapper(
      buildPortalAppConfig(
        authUser.app_config,
        resolvePortalLanguage(authUser.app_config),
      ),
    );
    // eslint-disable-next-line
  }, [authUser.company_status]);

  useEffect(() => {
    if (authUser && authUser.type === UserType.BusinessOwner) {
      setTimeout(() => {
        getinvoices()
          .then((response) => {
            if (response?.data?.success === false) {
              handleApiError(response, t);
              return;
            }

            let invoices = response.data.data.invoices || [];
            for (let i = 0; i < invoices.length; i++) {
              if (invoices[i].status === InvoiceStatus.Unpaid) {
                setAnyUnpaidInvoice(true);
                break;
              }
            }
          })
          .catch((error) => handleApiError(error, t));
      }, 2000);
    }
  }, [authUser, t]);

  useEffect(() => {
    const updateDeviceWidth = () => setDeviceWidth(window.innerWidth);

    window.addEventListener("resize", updateDeviceWidth);
    return () => window.removeEventListener("resize", updateDeviceWidth);
  }, []);

  useEffect(() => {
    const forcedPath = FORCED_PAGE_PATHS[showPageOnly];

    if (blockRouting && forcedPath && location.pathname !== forcedPath) {
      navigate(forcedPath, { replace: true });
    }
  }, [blockRouting, location.pathname, navigate, showPageOnly]);

  useEffect(() => {
    if (!activeMenuKey) return;

    localStorage.setItem(
      "last_visited_page",
      JSON.stringify({
        key: activeMenuKey,
        path: `${location.pathname}${location.search}`,
      }),
    );
  }, [activeMenuKey, location.pathname, location.search]);

  const logoutUser = async () => {
    try {
      setLogoutLoading(true);

      const response = await userLogout();

      if (response?.data?.success === false) {
        handleApiError(response, t);
        return;
      }

      localStorage.removeItem("last_visited_page");
      localStorage.removeItem("access_token");

      window.location = "/login";
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setLogoutLoading(false);
    }
  };

  const remainingDays = moment(authUser.created_at)
    .add(TRIAL_DURATION, "days")
    .diff(moment(), "days");
  const activeBranchId = getEntityBranchId(authUser);
  const isOrderManagementActive = authUser?.active_addons?.some(
    (addon) => addon?.name === AddonName.OrderManagement,
  );
  const isZatcaActive = authUser?.active_addons?.some(
    (addon) => addon?.name === AddonName.ZatcaEInvoicing,
  );
  const authWarnings = useMemo(
    () => normalizeWarnings(authUser?.warnings),
    [authUser?.warnings],
  );

  const UserOptions = [
    {
      key: "1",
      label: (
        <div
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => {
            navigator.clipboard
              ?.writeText(authUser.phone)
              .then(() => message.success(t("copied")))
              .catch(() => showAppError(t("copy_failed")));
          }}
        >
          <PhoneOutlined
            rotate={90}
            style={{ color: "var(--color-primary)" }}
          />{" "}
          <bdi dir="ltr">+{authUser.phone}</bdi>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div style={{ cursor: "auto" }}>
          <EnvironmentOutlined /> {authUser.branch_name}
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          onClick={() => {
            logoutUser();
          }}
        >
          <LogoutOutlined style={{ color: "var(--color-danger)" }} />{" "}
          {t("logout")}
        </div>
      ),
    },
  ];

  const handleLanguageChange = (language) => {
    const nextConfig = buildPortalAppConfig(authUser.app_config, language);
    const previousConfig = appConfig;
    setAppConfigWrapper(nextConfig);
    const formData = new FormData();
    formData.append("app_config", JSON.stringify(nextConfig));
    updateAuthDetails(formData)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setAppConfigWrapper(previousConfig);
          return;
        }
        setAuthUser((currentUser) => ({
          ...currentUser,
          app_config: nextConfig,
        }));
      })
      .catch((error) => {
        handleApiError(error, t);
        setAppConfigWrapper(previousConfig);
      });
  };

  const LangOptions = PORTAL_LANGUAGES.map(({ code, label }) => ({
    key: code,
    label: <div onClick={() => handleLanguageChange(code)}>{label}</div>,
  }));

  return (
    <Layout>
      {logoutLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "var(--color-white-soft)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" tip={t("logging_out")} />
        </div>
      )}
      {!fullScreenPOS && (
        <Header
          className="portal-dashboard-header"
          style={{
            backgroundColor: "var(--color-white)",
            position: "sticky",
            top: 0,
            zIndex: 100,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            lineHeight: "unset",
            padding: "0 20px",
            boxShadow: "0 4px 18px var(--color-shadow-soft)",
            maxHeight: "65px",
            height: "100vh",
          }}
        >
          <Space size={20} style={{ flexGrow: "1", justifyContent: "start" }}>
            {deviceWidth > 768 && (
              <Button
                type="default"
                onClick={() => setIsSiderCollapsed(!isSiderCollapsed)}
                shape="round"
                aria-label={
                  isSiderCollapsed ? "Expand sidebar" : "Collapse sidebar"
                }
                title={isSiderCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0",
                  height: "unset",
                }}
              >
                {appConfig.direction === "ltr" ? (
                  isSiderCollapsed ? (
                    <MenuUnfoldOutlined
                      style={{
                        fontSize: "20px",
                        display: "inline-flex",
                        backgroundColor: "var(--color-primary-soft)",
                        padding: "4px",
                        borderRadius: "35rem",
                        margin: "2px",
                      }}
                    />
                  ) : (
                    <MenuFoldOutlined
                      style={{
                        fontSize: "20px",
                        display: "inline-flex",
                        backgroundColor: "var(--color-primary-soft)",
                        padding: "4px",
                        borderRadius: "35rem",
                        margin: "2px",
                      }}
                    />
                  )
                ) : isSiderCollapsed ? (
                  <MenuFoldOutlined
                    style={{
                      fontSize: "20px",
                      display: "inline-flex",
                      backgroundColor: "var(--color-primary-soft)",
                      padding: "4px",
                      borderRadius: "35rem",
                      margin: "2px",
                    }}
                  />
                ) : (
                  <MenuUnfoldOutlined
                    style={{
                      fontSize: "20px",
                      display: "inline-flex",
                      backgroundColor: "var(--color-primary-soft)",
                      padding: "4px",
                      borderRadius: "35rem",
                      margin: "2px",
                    }}
                  />
                )}
              </Button>
            )}
            <img
              src="/anypos-logo.svg"
              alt={t("anypos_logo")}
              style={{
                width: "100%",
                maxWidth: "150px",
                height: "auto",
                cursor: "pointer",
                display: "block",
              }}
              onClick={() => {
                navigate("/home");
              }}
            />
            {authUser.business_name && (
              <Button
                type="default"
                shape="round"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0",
                  height: "unset",
                }}
                onClick={() => {
                  navigate("/store-configuration");
                }}
              >
                <ShopOutlined
                  style={{
                    fontSize: "20px",
                    display: "inline-flex",
                    backgroundColor: "var(--color-primary-soft)",
                    padding: "4px",
                    borderRadius: "35rem",
                    margin: "2px",
                  }}
                />
                <span style={{ margin: "0 8px" }}>
                  {authUser.business_name}
                </span>
              </Button>
            )}
          </Space>
          <Space size={20} style={{ flexGrow: "1", justifyContent: "center" }}>
            {process.env.REACT_APP_ENV_LABEL ? (
              <Alert
                message={process.env.REACT_APP_ENV_LABEL}
                type="warning"
                banner
                className="app-font-bold"
              />
            ) : process.env.REACT_APP_API_ROOT ===
              "https://api.qa.anypos.ai" ? (
              <Alert
                message={t("qa_server")}
                type="warning"
                banner
                className="app-font-bold"
              />
            ) : (
              ""
            )}
          </Space>
          <Space size={20} style={{ flexGrow: "1", justifyContent: "end" }}>
            {isOrderManagementActive && (
              <PendingQrOrderNotifications
                key={`${authUser.id || "unknown"}-${activeBranchId}`}
                branchId={activeBranchId}
                userId={authUser.id}
                direction={appConfig.direction}
              />
            )}
            <Dropdown
              trigger={["click"]}
              menu={{
                items: LangOptions,
              }}
            >
              <Button
                type="default"
                shape="round"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0",
                  height: "unset",
                }}
              >
                <GlobalOutlined
                  style={{
                    fontSize: "20px",
                    display: "inline-flex",
                    backgroundColor: "var(--color-primary-soft)",
                    padding: "4px",
                    borderRadius: "35rem",
                    margin: "2px",
                  }}
                />
                {portalLanguageLabel(resolvePortalLanguage(appConfig))}
                <span style={{ margin: "0 8px" }}>
                  <DownOutlined className="app-text-xs" />
                </span>
              </Button>
            </Dropdown>
            <Dropdown menu={{ items: UserOptions }} trigger={["click"]}>
              <Button
                type="primary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0",
                  height: "unset",
                }}
              >
                {authUser.photo ? (
                  <img
                    src={authUser.photo}
                    alt={authUser.name}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      backgroundColor: "var(--color-primary-soft)",
                      padding: "2px",
                      margin: "2px",
                    }}
                  />
                ) : (
                  <UserOutlined
                    style={{
                      fontSize: "20px",
                      display: "inline-flex",
                      padding: "4px",
                      borderRadius: "35rem",
                      margin: "2px",
                    }}
                  />
                )}

                {authUser.name + " "}
                <span style={{ margin: "0 8px" }}>
                  <DownOutlined className="app-text-xs" />
                </span>
              </Button>
            </Dropdown>
          </Space>
        </Header>
      )}
      <Layout className="app-relative">
        {showDesktopSidebar && (
          <Sider
            className="portal-dashboard-sider"
            collapsible
            theme="light"
            trigger={null}
            defaultCollapsed={false}
            collapsed={isSiderCollapsed}
            collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
            width={SIDEBAR_WIDTH}
            style={{
              boxShadow: "0 4px 18px var(--color-shadow-soft)",
              overflow: "auto",
              position: "fixed",
              left: appConfig.direction === "ltr" ? 0 : "unset",
              right: appConfig.direction === "ltr" ? "unset" : 0,
              top: 65,
              bottom: 0,
            }}
          >
            <Menu
              mode="inline"
              className="portal-dashboard-menu"
              selectedKeys={activeMenuKey ? [activeMenuKey] : []}
              items={sidebarPages
                .map((page) => {
                  if (page.showInSider) {
                    if (page.subMenus && page.subMenus.length > 0) {
                      return {
                        key: page.name,
                        icon: React.createElement(page.icon),
                        label: t(page.text),
                        title: t(page.text),
                        disabled: blockRouting,
                        children: page.subMenus.map((subPage) => ({
                          key: subPage.name,
                          icon: React.createElement(subPage.icon),
                          label: t(subPage.text),
                          title: t(subPage.text),
                          disabled: blockRouting,
                          onClick: blockRouting
                            ? undefined
                            : () => navigate(subPage.path),
                          className: "custom-menu-popup",
                        })),
                      };
                    } else {
                      return {
                        key: page.name,
                        icon: React.createElement(page.icon),
                        label: t(page.text),
                        title: t(page.text),
                        disabled: blockRouting,
                        onClick: blockRouting
                          ? undefined
                          : () => navigate(page.path),
                      };
                    }
                  }
                  return null;
                })
                .filter(Boolean)} // Filter out any null values that might have been returned
            />
          </Sider>
        )}
        <Layout
          style={{
            marginLeft: appConfig.direction === "ltr" ? sidebarOffset : 0,
            marginRight: appConfig.direction === "rtl" ? sidebarOffset : 0,
            transition: "margin 0.2s ease",
          }}
        >
          <Content
            style={{
              margin: "20px",
              padding: "0",
              background: "transparent",
              minHeight: fullScreenPOS
                ? "calc(100vh - 40px)"
                : "calc(100vh - 105px)",
            }}
          >
            {anyUnpaidInvoice && (
              <Alert
                message={t("unpaid_invoice")}
                description={t("pay_your_invoice")}
                type="error"
                showIcon
                className="app-mb-20"
                action={
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      navigate("/invoices");
                    }}
                  >
                    {t("show_invoices")}
                  </Button>
                }
              />
            )}
            {authUser.company_status === CompanyStatus.Kyc && (
              <Alert
                message={
                  t("kindly_complete_the_store_details_within") +
                  " " +
                  remainingDays +
                  " " +
                  t("days_otherwise_your_account_will_be_blocked")
                }
                type="warning"
                showIcon
                className="app-mb-20"
                action={
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      navigate("/store-configuration");
                    }}
                  >
                    {t("fill_the_details")}
                  </Button>
                }
              />
            )}
            {authUser.company_status === CompanyStatus.Review && (
              <Alert
                message={t("account_under_verification")}
                description={t("notified_once_verified")}
                type="info"
                showIcon
                className="app-mb-20"
              />
            )}
            {authUser.company_status === CompanyStatus.Blocked && (
              <Alert
                message={t("account_blocked")}
                description={t("kindly_contact_at")}
                type="error"
                showIcon
                className="app-mb-20"
              />
            )}
            {isZatcaActive && <ZatcaCsidExpiryBanner />}
            {authWarnings.map((warning, index) => {
              const messageText =
                warning?.message ||
                (typeof warning === "string" ? warning : null);
              if (!messageText) return null;

              const code = warning?.code;
              let actionButton = null;

              if (
                code === "zatca_onboarding_required" ||
                code === "zatca_onboarding"
              ) {
                actionButton = (
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      navigate("/zatca-onboarding");
                    }}
                  >
                    {t("zatca_onboarding")}
                  </Button>
                );
              } else if (code === "kyc_required") {
                actionButton = (
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      navigate("/store-configuration");
                    }}
                  >
                    {t("fill_the_details")}
                  </Button>
                );
              } else if (code === "unpaid_invoice") {
                actionButton = (
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => {
                      navigate("/invoices");
                    }}
                  >
                    {t("show_invoices")}
                  </Button>
                );
              }

              return (
                <Alert
                  key={code ? `${code}-${index}` : `auth-warning-${index}`}
                  message={messageText}
                  type="warning"
                  showIcon
                  className="app-mb-20"
                  action={actionButton}
                />
              );
            })}

            {blockRouting ? (
              <>
                {showPageOnly === "STORE_CONFIGURATION" && (
                  <StoreConfiguration />
                )}
                {showPageOnly === "HOME" && <Home />}
                {showPageOnly === "SUBSCRIPTION" && <Subscriptions />}
                {showPageOnly === "INVOICES" && <Invoices />}
              </>
            ) : (
              <AppRoutes />
            )}
          </Content>
          {/* {!fullScreenPOS && (
            <Footer
              style={{
                textAlign: "center",
                backgroundColor: "var(--color-white)",
                fontWeight: "500",
                padding: "10px",
              }}
            >
              AnyPOS © {new Date().getFullYear()}
            </Footer>
          )} */}
        </Layout>
      </Layout>
    </Layout>
  );
};
export default Dashboard;
